import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';

const BulkActionPanel = ({ 
  selectedIndividuals, 
  onClose, 
  onDelete, 
  onAddToProcess, 
  onAddToBizDevProcess,
  onSendEmail, 
  onAddTags, 
  onAddReminder 
}) => {
  const API = useContext(ApiContext);
  const [showSubMenu, setShowSubMenu] = useState(null);
  const [partnerPositions, setPartnerPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [bizDevProcesses, setBizDevProcesses] = useState([]);
  const [selectedBizDevProcess, setSelectedBizDevProcess] = useState('');
  const [recruitmentSteps, setRecruitmentSteps] =useState([]);
  const [loading, setLoading] = useState(false);

  // Récupérer tous les postes ouverts directement
  useEffect(() => {
    const fetchAllOpenPositions = async () => {
      try {
        const response = await API.get('/api/positions');
        const openPositions = response.data.filter(position => position.open);
        setPartnerPositions(openPositions);
      } catch (error) {
        console.error('Erreur lors de la récupération des postes:', error);
        setPartnerPositions([]);
      }
    };

    if (showSubMenu === 'process') {
      fetchAllOpenPositions();
    }
  }, [showSubMenu, API]);

  useEffect(() => {
    const fetchPositionSteps = async () => {
      if (selectedPosition && showSubMenu === 'process') {
        try {
          const response = await API.get(`/api/positions/${selectedPosition}`);
          const positionData = response.data;
          // Filtrer les étapes actives et les trier par ordre
          const activeSteps = positionData.steps?.filter(step => step.active)?.sort((a, b) => a.order - b.order) || [];
          setRecruitmentSteps(activeSteps);
        } catch (error) {
          console.error('Erreur lors de la récupération des étapes', error);
          setRecruitmentSteps([]);
        }
      }
    };

    fetchPositionSteps();
  }, [selectedPosition, showSubMenu, API])

  // Récupérer les processus BizDev actifs
  useEffect(() => {
    const fetchBizDevProcesses = async () => {
      try {
        const response = await API.get('/api/process-bizdev');
        const activeProcesses = response.data.filter(process => process.active);
        setBizDevProcesses(activeProcesses);
      } catch (error) {
        console.error('Erreur lors de la récupération des projets bizdev:', error);
      }
    };

    if (showSubMenu === 'bizdev') {
      fetchBizDevProcesses();
    }
  }, [showSubMenu, API]);
  
  const actions = [
    {
      id: 'delete',
      label: 'Supprimer les profils',
      icon: 'bi-trash',
      color: 'text-red-600',
      bgColor: 'hover:bg-red-50',
      onClick: () => {
        onDelete(selectedIndividuals);
        onClose();
      }
    },
    {
      id: 'process',
      label: 'Ajouter au projet de recrutement',
      icon: 'bi-arrow-right-circle',
      color: 'text-blue-600',
      bgColor: 'hover:bg-blue-50',
      hasSubMenu: true
    },
    {
      id: 'bizdev',
      label: 'Ajouter au projet de prospection',
      icon: 'bi-briefcase',
      color: 'text-indigo-600',
      bgColor: 'hover:bg-indigo-50',
      hasSubMenu: true
    },
    {
      id: 'email',
      label: 'Envoyer un email',
      icon: 'bi-envelope',
      color: 'text-green-600',
      bgColor: 'hover:bg-green-50',
      hasSubMenu: true
    },
    {
      id: 'tags',
      label: 'Ajouter des tags',
      icon: 'bi-tags',
      color: 'text-purple-600',
      bgColor: 'hover:bg-purple-50',
      hasSubMenu: true
    },
    {
      id: 'reminder',
      label: 'Ajouter une tâche',
      icon: 'bi-alarm',
      color: 'text-orange-600',
      bgColor: 'hover:bg-orange-50',
      hasSubMenu: true
    }
  ];

  const handleActionClick = (action) => {
    if (action.hasSubMenu) {
      setShowSubMenu(showSubMenu === action.id ? null : action.id);
    } else {
      action.onClick();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Actions pour {selectedIndividuals.length} profil(s)
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="bi bi-x-lg text-xl"></i>
            </button>
          </div>
        </div>

        {/* Actions List */}
        <div className="p-4 max-h-[calc(100vh-220px)] overflow-y-auto">
          {actions.map((action) => (
            <div key={action.id} className="mb-2 last:mb-0">
              <button
                onClick={() => handleActionClick(action)}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${action.bgColor} group`}
              >
                <div className="flex items-center">
                  <i className={`${action.icon} ${action.color} text-lg mr-3 group-hover:scale-110 transition-transform`}></i>
                  <span className="text-gray-700 font-medium">{action.label}</span>
                </div>
                {action.hasSubMenu && (
                  <i className={`bi bi-chevron-${showSubMenu === action.id ? 'up' : 'down'} text-gray-400`}></i>
                )}
              </button>

              {/* Sub-menus */}
              {showSubMenu === action.id && (
                <div className="ml-6 mt-2 bg-gray-50 rounded-lg p-3">
                  {action.id === 'process' && (
                    <div className="space-y-3">
                      {/* Sélection directe du poste ouvert */}
                      <div>
                        <select
                          value={selectedPosition}
                          onChange={(e) => setSelectedPosition(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Choisir un poste...</option>
                          {partnerPositions.map((position) => (
                            <option key={position._id} value={position._id}>
                              {position.title} - {position.partner?.name || 'Entreprise non spécifiée'} {position.salary ? `(${position.salary})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Étapes du processus */}
                      {selectedPosition && recruitmentSteps.length > 0 && (
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Choisir une étape :</label>
                          <div className="grid grid-cols-2 gap-1">
                            {recruitmentSteps.map((step) => (
                              <button
                                key={step._id}
                                onClick={() => {
                                  onAddToProcess(selectedIndividuals, selectedPosition, step._id);
                                  onClose();
                                }}
                                className="text-left px-2 py-1 text-xs text-gray-700 hover:bg-blue-100 rounded border border-gray-200"
                              >
                                {step.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedPosition && recruitmentSteps.length === 0 && (
                        <p className='text-xs text-gray-500 italic'>Aucune étape active pour ce poste</p>
                      )}

                      {partnerPositions.length === 0 && (
                        <p className="text-xs text-gray-500 italic">Aucun poste ouvert disponible</p>
                      )}
                    </div>
                  )}

                  {action.id === 'bizdev' && (
                    <BizDevSubMenu
                      selectedIndividuals={selectedIndividuals}
                      bizDevProcesses={bizDevProcesses}
                      selectedBizDevProcess={selectedBizDevProcess}
                      setSelectedBizDevProcess={setSelectedBizDevProcess}
                      onAddToBizDevProcess={onAddToBizDevProcess}
                      onClose={onClose}
                    />
                  )}

                  {action.id === 'email' && (
                    <EmailSubMenu
                      selectedIndividuals={selectedIndividuals}
                      onSendEmail={onSendEmail}
                      onClose={onClose}
                    />
                  )}

                  {action.id === 'tags' && (
                    <TagsSubMenu 
                      selectedIndividuals={selectedIndividuals}
                      onAddTags={onAddTags}
                      onClose={onClose}
                      API={API}
                    />
                  )}

                  {action.id === 'reminder' && (
                    <ReminderSubMenu 
                      selectedIndividuals={selectedIndividuals}
                      onAddReminder={onAddReminder}
                      onClose={onClose}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant pour l'envoi d'email avec choix du type
const EmailSubMenu = ({ selectedIndividuals, onSendEmail, onClose }) => {
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-600 mb-2">Choisir le type d'email :</p>
      <div className="space-y-2">
        <button
          onClick={() => {
            onSendEmail('professional');
          }}
          className="w-full text-left px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-all flex items-center gap-2"
        >
          <i className="bi bi-briefcase text-blue-600"></i>
          <div>
            <div className="font-medium">Email professionnel</div>
            <div className="text-xs text-gray-500">Uniquement les contacts avec email pro</div>
          </div>
        </button>

        <button
          onClick={() => {
            onSendEmail('personal');
          }}
          className="w-full text-left px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-green-50 hover:border-green-400 transition-all flex items-center gap-2"
        >
          <i className="bi bi-person text-green-600"></i>
          <div>
            <div className="font-medium">Email personnel</div>
            <div className="text-xs text-gray-500">Uniquement les contacts avec email perso</div>
          </div>
        </button>
      </div>
    </div>
  );
};

// Composant pour l'ajout de tags
const TagsSubMenu = ({ selectedIndividuals, onAddTags, onClose, API }) => {
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAddTags = async () => {
    if (!tagInput.trim()) return;
    
    setLoading(true);
    try {
      const tags = tagInput.split(',').map(tag => tag.trim()).filter(tag => tag);
      if (tags.length > 0) {
        await onAddTags(selectedIndividuals, tags);
        onClose();
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout des tags:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-600 mb-2">Ajouter des tags :</p>
      <div className="space-y-2">
        <input
          type="text"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          placeholder="Séparer par des virgules (ex: javascript, senior, remote)"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleAddTags();
            }
          }}
        />
        <div className="flex justify-end">
          <button
            onClick={handleAddTags}
            disabled={!tagInput.trim() || loading}
            className="px-3 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white text-sm rounded transition-colors"
          >
            {loading ? 'Ajout...' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant pour l'ajout de rappels
const ReminderSubMenu = ({ selectedIndividuals, onAddReminder, onClose }) => {
  const [reminderDate, setReminderDate] = useState('');
  const [reminderNote, setReminderNote] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAddReminder = async () => {
    if (!reminderDate) return;
    
    setLoading(true);
    try {
      await onAddReminder(selectedIndividuals, reminderDate, reminderNote);
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'ajout du rappel:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-600 mb-2">Créer une tâche :</p>
      <div className="space-y-2">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Date :</label>
          <input
            type="date"
            value={reminderDate}
            onChange={(e) => setReminderDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Note :</label>
          <textarea
            value={reminderNote}
            onChange={(e) => setReminderNote(e.target.value)}
            placeholder="Tâche pour suivi candidats..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
            rows={2}
          />
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleAddReminder}
            disabled={!reminderDate || loading}
            className="px-3 py-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white text-sm rounded transition-colors"
          >
            {loading ? 'Création...' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant pour l'ajout au processus BizDev
const BizDevSubMenu = ({ 
  selectedIndividuals, 
  bizDevProcesses, 
  selectedBizDevProcess, 
  setSelectedBizDevProcess, 
  onAddToBizDevProcess, 
  onClose 
}) => {
  const [loading, setLoading] = useState(false);

  const handleAddToBizDev = async (step) => {
    if (!selectedBizDevProcess) return;
    
    setLoading(true);
    try {
      await onAddToBizDevProcess(selectedIndividuals, selectedBizDevProcess, step);
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'ajout au processus BizDev:', error);
    } finally {
      setLoading(false);
    }
  };

  // Trouver le processus sélectionné pour récupérer ses étapes actives
  const selectedProcess = bizDevProcesses.find(process => process._id === selectedBizDevProcess);
  const activeSteps = selectedProcess?.steps?.filter(step => step.active) || [];

  return (
    <div className="space-y-3">
      {/* Sélection du processus BizDev */}
      <div>
        <select
          value={selectedBizDevProcess}
          onChange={(e) => setSelectedBizDevProcess(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="">Choisir un projet...</option>
          {bizDevProcesses.map((process) => (
            <option key={process._id} value={process._id}>
              {process.name}
            </option>
          ))}
        </select>
      </div>

      {/* Étapes du processus BizDev */}
      {selectedBizDevProcess && activeSteps.length > 0 && (
        <div>
          <label className="block text-xs text-gray-600 mb-1">Choisir une étape :</label>
          <div className="grid grid-cols-2 gap-1">
            {activeSteps.map((step) => (
              <button
                key={step.name}
                onClick={() => handleAddToBizDev(step.name)}
                disabled={loading}
                className="text-left px-2 py-1 text-xs text-gray-700 hover:bg-indigo-100 rounded border border-gray-200 disabled:opacity-50"
              >
                {step.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedBizDevProcess && activeSteps.length === 0 && (
        <p className="text-xs text-gray-500">Aucune étape active dans ce processus.</p>
      )}
    </div>
  );
};

export default BulkActionPanel;